import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { constants } from "../constants";
import { Language } from "../models/language.model";
import { Translation } from "../models/translation.model";

@Injectable({ providedIn: 'root' })
export class LocalizationService {

  private static readonly LOCALIZATION_URL: string = `${constants.BASE_URL}/localization`;

  private loadingTranslations: boolean = true;
  private language$: BehaviorSubject<Language> = new BehaviorSubject<Language>(new Language({}));
  private translationsMap: Map<string, string> = new Map();

  constructor(private http: HttpClient) { }

  getUserLanguages(): Observable<Language[]> {
    return this.http.get<Language[]>(`${LocalizationService.LOCALIZATION_URL}/languages`)
      .pipe(
        map((response: any[]) => response.map(languages => new Language(languages))),
        map(languages => this.sortLanguages(languages))
      );
  }

  getTranslations(language: Language): void {
    this.loadingTranslations = true;

    const isCountryLanguageTranslationAvailable: boolean = language.isCountryLanguageTranslationAvailable();
    const translationUrl: string = `${isCountryLanguageTranslationAvailable ? 'country-' : ''}language/${isCountryLanguageTranslationAvailable ? language.getCountryLanguageId() : language.getId()}`;

    this.http.get<Array<Translation>>(`${LocalizationService.LOCALIZATION_URL}/${translationUrl}`)
      .pipe(map((response: any[]) => response.map(translation => new Translation(translation))))
      .subscribe(translations => {
        this.translationsMap = this.createTranslationsMap(translations);
        this.loadingTranslations = false;
      });
  }

  updateLanguageSettings(language: Language): void {
    this.getTranslations(language);
    this.language$.next(language);
  }

  getLanguage(): Observable<Language> {
    return this.language$.asObservable();
  }

  getTranslationsMap(): Map<string, string> {
    return this.translationsMap;
  }

  isLoadingTranslations(): boolean {
    return this.loadingTranslations;
  }

  private createTranslationsMap(translations: Translation[]): Map<string, string> {
    const translationsMap: Map<string, string> = new Map();
    translations.forEach(translation => {
      translationsMap.set(translation.getCode(), translation.getText());
    });
    return translationsMap;
  }

  private sortLanguages(languages: Language[]): Language[] {
    return languages
      .sort((a, b) => {
        if (a.getCountryCode() !== b.getCountryCode()) {
          return a.getCountryCode().localeCompare(b.getCountryCode());
        }

        return a.getOrder() - b.getOrder();
      });
  }

}
